import React, { createContext, useContext } from 'react';

import { withEventCatcher } from '@repo/event-processor';
import cn from 'classnames';

import { utilityCSSVariables } from '../../../css/utils';
import { Spinner } from '../Spinner';
import { H4, H5, H6, H7 } from '../Typography';

import { BaseButton } from './BaseButton';
import styles from './Button.module.css';
import type { ButtonProps, ButtonLabelProps } from './types';

const getLabelComponent = (size: ButtonProps['size']) => {
  switch (size) {
    case 'xs':
      return H7;
    case 's':
      return H6;
    case 'l':
      return H4;
    case 'm':
    default:
      return H5;
  }
};

const getIconSize = (size: ButtonProps['size']) => {
  switch (size) {
    case 'l':
      return 20;
    case 'xs':
      return 12;
    case 'm':
    case 's':
    default:
      return 16;
  }
};

// Create a context for size
const ButtonSharedPropsContext = createContext<{ size: ButtonProps['size'] }>({
  size: 'm',
});

const ButtonLabel: React.FC<ButtonLabelProps> = ({
  children,
  justifyContent = 'center',
}) => {
  const { size } = useContext(ButtonSharedPropsContext);
  const Label = getLabelComponent(size);

  if (!children) {
    return null;
  }

  return (
    <div
      className={cn(styles['button-label'], {
        [styles['alignable-label']]: justifyContent,
      })}
      style={{ justifyContent: justifyContent }}
    >
      <Label
        bold
        textOverflowEllipsis
        as="span"
        color="inherit"
        margin="0 var(--spacing-4)"
      >
        {children}
      </Label>
    </div>
  );
};

const _Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      children,
      ariaLabel,
      variant = 'primary',
      size = 'm',
      className,
      disabled,
      isLoading,
      ...rest
    },
    ref
  ) => {
    const iconSize = getIconSize(size);

    // Add validation check
    const hasButtonLabel = React.Children.toArray(children).some(
      (child) => React.isValidElement(child) && child.type === ButtonLabel
    );

    if (!hasButtonLabel && !ariaLabel) {
      throw new Error(
        'Button must contain either Button.Label component or aria-label prop for accessibility'
      );
    }

    return (
      <ButtonSharedPropsContext.Provider value={{ size }}>
        <BaseButton
          {...rest}
          ref={ref}
          aria-label={ariaLabel}
          className={cn(
            className,
            styles[`variant-${variant}`],
            styles[`size-${size}`]
          )}
          disabled={disabled || isLoading}
        >
          {isLoading ? (
            <Spinner color="currentColor" size={iconSize} />
          ) : (
            LeftIcon && (
              <span aria-hidden="true">
                <LeftIcon
                  activeColor="currentColor"
                  color="currentColor"
                  size={iconSize}
                  transitionDuration={utilityCSSVariables.transition}
                />
              </span>
            )
          )}

          {children}

          {RightIcon && (
            <span aria-hidden="true">
              <RightIcon
                activeColor="currentColor"
                color="currentColor"
                size={iconSize}
                transitionDuration={utilityCSSVariables.transition}
              />
            </span>
          )}
        </BaseButton>
      </ButtonSharedPropsContext.Provider>
    );
  }
);

_Button.displayName = 'Button';

const ButtonWithEventCatcher = withEventCatcher(_Button);

export const Button = Object.assign(ButtonWithEventCatcher, {
  Label: ButtonLabel,
});
